import { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { TabSet, Spinner, Buttons, Modal } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";

import { getProviderManagementAPI } from "@/api";
import { ProviderManagement } from "@/api/interfaces";
import { AuthenticationContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";

import { ContactInformationView } from "./ContactInformation/ContactInformationView";
import { DetailsView } from "./Details/DetailsView";
import { NetworkList } from "./PaymentRemittance/NetworkList";
import { DetailsEdit } from "./Details/DetailsEdit";
import { ContactInformationEdit } from "./ContactInformation/ContactInformationEdit";

export const ProviderTabs = () => {
  const { getAuthToken } = useContext(AuthenticationContext);

  const [provider, setProvider] = useState<ProviderManagement.ProviderSchema>();
  const [documents, setDocuments] = useState<ProviderManagement.DocumentSchema[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabIdFromURL = searchParams.get("tab") !== undefined ? searchParams.get("tab") : "details";
  const [tabId, setTabId] = useState(tabIdFromURL);

  const [isEditDetails, setIsEditDetails] = useState(false);
  const [isEditContact, setIsEditContact] = useState(false);

  const [discardModalVisible, setDiscardModalVisible] = useState(false);
  const [pendingTabId, setPendingTabId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<"tab-change" | "cancel" | null>(null);

  const switchTab = (newTabId: string) => {
    setIsEditDetails(false);
    setIsEditContact(false);
    setTabId(newTabId);
    setSearchParams({ tab: newTabId });
  };

  const handleTabChange = (newTabId: string) => {
    if (isEditDetails || isEditContact) {
      setPendingAction("tab-change");
      setPendingTabId(newTabId);
      setDiscardModalVisible(true);
    } else {
      switchTab(newTabId);
    }
  };

  const handleCancelClick = () => {
    setPendingAction("cancel");
    setDiscardModalVisible(true);
  };

  const handleDiscardConfirm = (approved: boolean) => {
    setDiscardModalVisible(false);
    if (approved) {
      setIsEditDetails(false);
      setIsEditContact(false);
      if (pendingAction === "tab-change" && pendingTabId) {
        switchTab(pendingTabId);
      }
    }
    setPendingAction(null);
    setPendingTabId(null);
  };

  const getProvider = useCallback(async () => {
    setIsLoading(true);

    try {
      // Find provider management record and process response
      const pmResponse = await getProviderManagementAPI(getAuthToken());
      setProvider(pmResponse.provider);

      setDocuments(pmResponse.documents ?? []);
    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    getProvider();
  }, [getProvider]);

  return (
    <Stack spacing="large">
      <TabSet selectedTab={tabId} onSelectedTabChange={handleTabChange}>
        <TabSet.Tab id="details"><Subheading>Provider Details</Subheading></TabSet.Tab>
        <TabSet.Tab id="contact"><Subheading>Contact Information</Subheading></TabSet.Tab>
        <TabSet.Tab id="payment"><Subheading>Network & Payment Preferences</Subheading></TabSet.Tab>
      </TabSet>

      {isLoading && <Stack alignment="center"><Spinner size="large"/></Stack>}

      {tabId === "details" && provider && !isLoading && !isEditDetails && (
        <DetailsView
          provider={provider}
          setProvider={setProvider}
          documents={documents}
          setIsEditDetails={setIsEditDetails}
        />
      )}

      {tabId === "details" && provider && !isLoading && isEditDetails && (
        <DetailsEdit
          provider={provider}
          documents={documents}
          setDocuments={setDocuments}
          setProvider={setProvider}
          setIsEditDetails={setIsEditDetails}
          onCancel={handleCancelClick}
        />
      )}
      {tabId === "contact" && provider && !isLoading && !isEditContact && (
        <ContactInformationView provider={provider} setIsEditContact={setIsEditContact} />
      )}
      {tabId === "contact" && provider && !isLoading && isEditContact && (
        <ContactInformationEdit provider={provider} setProvider={setProvider} setIsEditContact={setIsEditContact} onCancel={handleCancelClick} />
      )}
      {tabId === "payment" && provider && !isLoading && <NetworkList provider={provider} />}
      <Modal.Approve
        title="Discard Changes"
        onClose={handleDiscardConfirm}
        visible={discardModalVisible}
        approveButtonAppearance={Buttons.Destructive.Primary.Large}
        denyContent="Cancel"
        approveContent="Discard"
      >
        <Body>Unsaved changes will be lost if you proceed.</Body>
      </Modal.Approve>
    </Stack>
  );
};
